import React, { useState, useEffect } from 'react';
import { useAllData } from './hooks/useAllData';
import { usePageNumber } from './hooks/usePageNumber';
import { useAllBoards } from './hooks/useAllBoards';
import { useDeleted } from './hooks/useDeleted';
import { useSelectedBoards } from './hooks/useSelectedBoards';
import { useGroups } from './hooks/useGroups';
import { useSearchText } from './hooks/useSearchText';
import { useRole } from './hooks/useRole';
import { ConfirmDelete } from './ConfirmDelete';
import { Form, Input, Button, InputGroup, Alert, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import '../stylesheets/custom.css';
import '../stylesheets/dataPage.css';
import '../stylesheets/alert.css';
import '../stylesheets/buttons.css';
import { BoardDataTable } from './BoardDataTable';
import { Pagination } from './Pagination';
import { Download } from './Download';
import { DropdownFilter } from './DropdownFilter';
import { useParams } from 'react-router-dom';
import { useAllCompanies } from './hooks/useAllCompanies';
import { GraphTypeSelect } from './GraphTypeSelect';   // ← Keeps your existing modal

export function BoardDataPage() {
    const { companyId, pcbSN } = useParams();
    const { allData, getAllData } = useAllData();
    const { allBoards, getAllBoards, pagedBoards, getPagedBoards, removeFromPagedBoards, addToPagedBoards, resetPagedBoards } = useAllBoards();
    const { selectedBoards, addToSelectedBoards, removeFromSelectedBoards, selectedAll, rowCount } = useSelectedBoards();
    const { allGroups, companyGroups, getAllGroupNames, getGroupsByCompany } = useGroups();
    const { pageNumber, incrementPageNumber, decrementPageNumber, resetPageNumber, lastPageNum, updateLastPageNum } = usePageNumber();
    const { deleted, isDeleted, isNotDeleted } = useDeleted();
    const { searchText, updateSearchText } = useSearchText();
    const { getAllCompanies, allCompanies } = useAllCompanies();

    const [deleteModal, setDeleteModal] = useState(false);
    const [allSelected, setAllSelected] = useState(false);
    const [whichGroup, setWhichGroup] = useState('All Data');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
    const [isSelected, setIsSelected] = useState(false);

    // Modal state for Graph Type Selection
    const [graphTypeModalOpen, setGraphTypeModalOpen] = useState(false);
    const toggleGraphTypeModal = () => setGraphTypeModalOpen(!graphTypeModalOpen);

    // NEW: Track currently selected company to pass down to GraphDataSelect via GraphTypeSelect
    const [selectedCompanyIdForGraph, setSelectedCompanyIdForGraph] = useState(null);

    const token = localStorage.getItem("token");
    var base64Payload = token.split('.')[1];
    var payload = window.atob(base64Payload);
    var payloadValues = payload.valueOf();
    var parsedPayload = JSON.parse(payloadValues);
    const role = parsedPayload.UserRole;
    const userCompanyId = parsedPayload.CompanyId;
    const currentUser = parsedPayload.UserName;

    // Computed current company ID (from URL param or dropdown selection)
    const currentCompanyId = companyId || selectedCompanyIdForGraph;

    useEffect(() => {
        var pageUrl = ``;

        if (companyId && isSelected === false) {
            pageUrl = `api/boardData/company/${companyId}/${pageNumber}`;
            setSelectedCompanyIdForGraph(companyId);   // Sync for graphing
        } else if (isSelected === true) {
            pageUrl = `api/boarddata/group/${whichGroup}/${pageNumber}`;
        } else if (pcbSN) {
            pageUrl = `api/boardData/pcbSn/${pcbSN}/${pageNumber}`;
        } else {
            pageUrl = `api/boardData/page/${pageNumber}`;
        }

        getPagedBoards(pageUrl);

        if (lastPageNum == null || pageNumber == lastPageNum) {
            var lpn = (pagedBoards.length >= 20) ? pageNumber + 1 : pageNumber;
            updateLastPageNum(lpn);
        }

    }, [pageNumber, pagedBoards, whichGroup, companyId]);

    const deleteToggle = () => {
        setDeleteModal(!deleteModal);
    }

    const updateWhichGroup = (e) => {
        setWhichGroup(e);

        if (allSelected) {
            resetPagedBoards(selectedBoards.slice(0, 20));
            selectedAll([]);
            setAllSelected(false);
        }

        resetPageNumber();
        selectedAll([]);

        if (e === "All Data") {
            setIsSelected(false);
        } else {
            setIsSelected(true);
        }
    }

    const toggleFilterByGroup = () => {
        if (companyId) {
            getGroupsByCompany(companyId);
        } else {
            getAllGroupNames();
        }
        setDropdownOpen(!dropdownOpen);
    }

    const toggleFilterByCompany = () => {
        getAllCompanies(`api/companies/`);
        setCompanyDropdownOpen(!companyDropdownOpen);
    }

    // NEW: Handle company selection and keep it in sync for graphing
    const handleCompanySelect = (compId) => {
        setSelectedCompanyIdForGraph(compId);

        if (compId) {
            window.location.href = `/boarddata/${compId}`;
        } else {
            window.location.href = '/boarddata';
        }
    }

    const handleBoardSelected = (b, e) => {
        if(e.target.checked) {
            addToSelectedBoards(b);
            removeFromPagedBoards(b);
        } else {
            removeFromSelectedBoards(b);
            addToPagedBoards(b);
            setAllSelected(false);
        }
    }

    const handleAllSelected = (e) => {
        if (e.target.checked) {
            selectedAll(selectedBoards.concat(pagedBoards));
            resetPagedBoards([]);
            setAllSelected(true);
        } else {
            resetPagedBoards(selectedBoards.slice(0, 20));
            selectedAll([]);
            setAllSelected(false);
        }
    }

    const deleteSelectedData = () => {
        var selectedIds = selectedBoards.map(x => x.id);
        fetch(`api/boardData/bulkdelete`, {
            method: "DELETE",
            body: JSON.stringify({ ids: selectedIds }),
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-type": "application/json; charset=UTF-8"
            }
        })
        .then(resp => resp.ok ? isDeleted() : isNotDeleted())
        .catch(e => console.log(e))
    }

    const handleSearch = (e) => {
        updateSearchText(e.target.value);
        getAllBoards(`api/boardData/search/${e.target.value}/${pageNumber}`);
    }

    if(deleted === null) {
        return (
            <Alert className='alert-alignment' color='danger'>Something went wrong.
                <Button color='link' className='alert-link' href='/boarddata'>Back</Button>
            </Alert>
        );
    } else if(deleted === true) {
        return (
            <Alert className='alert-alignment' color='success'>Data successfully deleted.
                <Button color='link' className='alert-link' href='/boarddata'>Back</Button>
            </Alert>
        );
    }

    return (
        <React.Fragment>
            <div className='half-page'>
                <h3>{role === 'ReadOnly' || role === 'EndClient' || role === 'GuestClient' ? "Select data to download:" : "Select data to download / delete:"}</h3>
                <div className="buttonList">
                    <Button color='primary' className={companyId || pcbSN ? 'button-list' : 'hidden'} href='/boarddata'>View All Data</Button>

                    {/* Filter By Company Dropdown */}
                    <Dropdown className={companyId || pcbSN || role === 'Client' || role === 'ReadOnly' || role === 'Tech' || role === 'EndClient' || role === 'GuestClient' ? 'hidden' : ''}
                              isOpen={companyDropdownOpen}
                              toggle={toggleFilterByCompany}>
                        <DropdownToggle color='primary' className='button-list' caret>
                            Filter By Company
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem header>Companies</DropdownItem>
                            <DropdownItem
                                onClick={() => handleCompanySelect(null)}
                                active={!currentCompanyId}
                            >
                                All Companies
                            </DropdownItem>
                            {allCompanies.map(c => (
                                <DropdownItem
                                    key={c.id}
                                    onClick={() => handleCompanySelect(c.id)}
                                    active={String(currentCompanyId) === String(c.id)}
                                >
                                    {c.companyName}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>

                    <DropdownFilter filterButtonClass={pcbSN ? 'hidden' : 'primaryColor button-list'} dropdownOpen={dropdownOpen}
                                    onToggle={toggleFilterByGroup} listItems={companyId ? companyGroups : allGroups}
                                    onFilter={updateWhichGroup} buttonText="Filter By Group" listHeader="Groups" />

                    <ConfirmDelete
                        deleteButtonClass={role === 'ReadOnly' || role === 'EndClient' || role === 'GuestClient' ? 'hidden' : 'button-list'}
                        deleteMessage="the selected data from the database."
                        deleteSelected={deleteSelectedData}
                        buttonLabel="Delete Selected"
                        deleteModal={deleteModal}
                        deleteToggle={deleteToggle}
                        btnDisabled={rowCount < 1}
                    />
                    <Download
                        jsonData={selectedBoards.length === 0 ? allBoards : selectedBoards}
                        buttonText="Download CSV"
                        fileName='EDG_BoardData'
                        showLabel='true'
                        className='btn btn-info downloadButton button-list'
                        btnDisabled={rowCount < 1}
                    />
                    <Button className='button-list downloadButton' href='/dataRangeSelect'>Download Range to CSV</Button>

                    {/* Graph Data button opens GraphTypeSelect */}
                    <Button
                        color="primary"
                        className="button-list"
                        onClick={toggleGraphTypeModal}
                    >
                        Graph Data
                    </Button>
                </div>
                <InputGroup className={companyId || pcbSN || isSelected ? 'hidden' : ''}>
                    <Input id="text" placeholder="Search" value={searchText} onChange={e => handleSearch(e)} />
                </InputGroup>
            </div>

            <Form>
                <Alert color='primary' className={rowCount === 0 ? 'hidden' : 'table-header'}>
                    Selected: <span className='font-weight-bold'>{rowCount}</span> rows of data.
                </Alert>
                <BoardDataTable
                    userRole={role}
                    allSelected={allSelected}
                    showCheckboxes={true}
                    selectAllDisabled={false}
                    title={pcbSN ? pcbSN : whichGroup}
                    checkedBoards={selectedBoards.slice(0, 20 - pagedBoards.length)}
                    boards={pagedBoards}
                    onBoardSelected={handleBoardSelected}
                    onAllSelected={handleAllSelected}
                />
                <Pagination
                    currentPage={pageNumber}
                    previous={decrementPageNumber}
                    next={incrementPageNumber}
                    lastPage={(pageNumber >= lastPageNum || pagedBoards.length < 20)}
                    selectedAll={(selectedBoards.length > 0 || allSelected)}
                />
            </Form>

            {/* Graph Type Selection Modal - now receives the current company filter */}
            <GraphTypeSelect
                isOpen={graphTypeModalOpen}
                toggle={() => setGraphTypeModalOpen(false)}
                selectedCompanyId={selectedCompanyIdForGraph}
                currentUsername={currentUser}        // or however you store logged-in user
                currentCompanyId={userCompanyId}   // Pass the user's company ID for feature checks
            />
        </React.Fragment>
    );
}